import React, { useEffect } from "react";
import FAQSection from '../components/FAQSection';
import { Link } from 'react-router-dom';
import ServicesSection from "../components/ServicesSection";
import { Helmet } from "react-helmet";


const Services = () => {
    useEffect(() => {
        // Set document title for SEO
        document.title = "MM AutoTech | Digital Solutions for UK Garages & Workshops";

        // Set or create meta description tag
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute(
                "content",
                "MM AutoTech offers expert digital marketing, customer service, lead generation, and logistics tailored to UK garages."
            );
        } else {
            const desc = document.createElement("meta");
            desc.name = "description";
            desc.content =
                "MM AutoTech offers expert digital marketing, customer service, lead generation, and logistics tailored to UK garages.";
            document.head.appendChild(desc);
        }

        // Set or create meta keywords tag
        const metaKeywords = document.querySelector('meta[name="keywords"]');
        if (metaKeywords) {
            metaKeywords.setAttribute(
                "content",
                "MM AutoTech, digital marketing for garages, garage customer service, UK automotive workshops"
            );
        } else {
            const keywords = document.createElement("meta");
            keywords.name = "keywords";
            keywords.content =
                "MM AutoTech, digital marketing for garages, garage customer service, UK automotive workshops";
            document.head.appendChild(keywords);
        }
    }, []);
    const faqData = [
        {
            q: "What types of garages do you work with?",
            a: "We work with independent MOT centres, body shops, mobile mechanics, and multi-site repair chains."
        },
        {
            q: "Can I use just one of your services?",
            a: "Yes — all of our services are modular. You can start with one and scale as needed."
        },
        {
            q: "Do you provide white-label customer service?",
            a: "Absolutely. We operate under your garage’s brand to offer a seamless experience to your clients."
        },
        {
            q: "How quickly can I start seeing results?",
            a: "Some results are visible in as little as 2–4 weeks depending on the service and your current setup."
        },
        {
            q: "Are your teams based in the UK?",
            a: "Yes — our marketing, support, and operations teams are all UK-based for local expertise."
        }
    ];

    return (

        <><Helmet>
            <title>Our Services | MM AutoTech</title>
            <meta
                name="description"
                content="Discover all services offered by MM AutoTech: lead generation, parts sourcing, admin support, websites, and digital marketing for UK garages."
            />
            <link rel="canonical" href="https://mmautotech.com/services" />
        </Helmet>
            <div className="max-w-7xl mx-auto space-y-24">
                {/* Hero / Intro */}
                <section className="flex flex-col lg:flex-row items-stretch">
                    {/* Left Content */}
                    <div className="lg:w-1/2 p-8 flex flex-col justify-center">
                        <span className="text-sm text-blue-900 font-semibold mb-3 uppercase tracking-widest">
                            Our Services
                        </span>

                        {/* Page H1 */}
                        <h1 className="text-3xl sm:text-4xl font-extrabold leading-snug mb-6 text-blue-900">
                            Services for UK Garages &amp; Workshops
                        </h1>

                        <div className="w-12 border-b-2 border-yellow-400 mb-6" />

                        <p className="text-base text-blue-800 font-medium mb-8">
                            We help you grow with data-driven marketing, white-label customer service,
                            and precision logistics—built specifically for UK automotive businesses.
                        </p>

                        {/* Link styled as button (no nested interactive) */}
                        <Link
                            to="/book-appointment"
                            className="inline-flex items-center justify-center text-sm font-semibold tracking-widest text-blue-900 border border-blue-900 px-6 py-2 rounded hover:bg-yellow-400 hover:border-yellow-400 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30"
                            aria-label="Book an appointment"
                        >
                            Book an Appointment
                        </Link>
                    </div>

                    {/* Right Image */}
                    <div
                        className="lg:w-1/2 h-64 lg:h-auto relative overflow-hidden"
                        style={{ clipPath: "polygon(15% 0, 100% 0, 100% 100%, 0% 100%)" }} // clip the container
                    >
                        <img
                            src="/assets/Services/1.png"
                            alt="Technician assisting a UK garage at the service desk"
                            width="1280"
                            height="960"
                            loading="eager"
                            fetchpriority="high"
                            decoding="async"
                            className="w-full h-full object-cover" />
                        {/* Overlay replaces filter: cheaper, consistent contrast */}
                        <div
                            className="absolute inset-0 bg-black/35 pointer-events-none"
                            aria-hidden="true" />
                    </div>
                </section>

                {/* Section 2: Tailored B2B Services for Automotive Growth */}
                <section className="max-w-7xl mx-auto px-6 py-6 lg:py-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">

                        {/* Left Image */}
                        <div className="relative">
                            <img
                                src="/assets/Services/2.png"
                                alt="Fleet service technology dashboard"
                                className="w-full h-auto rounded-xl shadow-xl object-cover" />
                            <div className="absolute inset-0 bg-black/25 rounded-xl pointer-events-none" aria-hidden="true" />
                        </div>

                        {/* Right Content */}
                        <div>
                            <h2 className="text-4xl font-extrabold text-blue-900 mb-4">
                                Tailored B2B Services<br className="hidden sm:block" /> for Automotive Growth
                            </h2>

                            <div className="w-14 h-1 bg-yellow-400 mb-6 rounded" />

                            <p className="text-base text-gray-700 mb-6 leading-relaxed">
                                Whether you're an independent MOT centre or a multi-site repair chain, MM AutoTech delivers
                                industry-specific business solutions to fuel your growth. We work exclusively within the UK
                                automotive sector — so we speak your language and know your pain points.
                            </p>

                            <ul className="space-y-4 text-gray-800 text-base list-disc list-inside font-medium">
                                <li>
                                    <strong className="text-blue-900">Lead Generation that Converts:</strong> We bring in qualified local customers actively searching for your services.
                                </li>
                                <li>
                                    <strong className="text-blue-900">Customer Service at Scale:</strong> White-labelled, UK-based support teams ready to manage calls and bookings.
                                </li>
                                <li>
                                    <strong className="text-blue-900">Digital Campaigns Built for Garages:</strong> SEO, PPC, and reviews tailored to regional search behavior.
                                </li>
                                <li>
                                    <strong className="text-blue-900">Parts & Procurement Support:</strong> Fast, affordable sourcing of OEM and aftermarket parts.
                                </li>
                            </ul>

                            <p className="mt-6 text-sm text-blue-800 font-medium italic">
                                All services are modular, white-labelled, and built to grow with your business.
                            </p>
                        </div>
                    </div>
                </section>

                <ServicesSection title="Solutions Tailored for Your Automotive Business" />

                {/* Why Choose MM AutoTech Section */}
                <section className="bg-gray-50 py-20 px-6">
                    <div className="max-w-7xl mx-auto text-center">
                        {/* Section Title */}
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-blue-900 mb-4">
                            Why UK Garages Choose MM AutoTech
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto mb-12 text-lg">
                            We're not just a service provider — we're your growth partner. Here’s why leading MOT centres and workshops rely on us.
                        </p>

                        {/* Cards Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
                            {/* Card 1 */}
                            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-blue-800 text-xl font-semibold mb-2"> Industry-Specific Focus</h3>
                                <p className="text-gray-700">
                                    We serve only the automotive sector — ensuring solutions that truly match garage operations and customer demands.
                                </p>
                            </div>

                            {/* Card 2 */}
                            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-blue-800 text-xl font-semibold mb-2">🇬🇧 UK-Based Teams</h3>
                                <p className="text-gray-700">
                                    From lead gen to phone support, our UK-based teams ensure speed, relevance, and better customer satisfaction.
                                </p>
                            </div>

                            {/* Card 3 */}
                            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-blue-800 text-xl font-semibold mb-2"> All-in-One Partner</h3>
                                <p className="text-gray-700">
                                    Avoid juggling agencies. We handle sales funnels, admin, marketing, software, and procurement under one roof.
                                </p>
                            </div>

                            {/* Card 4 */}
                            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-blue-800 text-xl font-semibold mb-2"> Transparent ROI</h3>
                                <p className="text-gray-700">
                                    Get clear reporting and no hidden fees. You’ll always know your return on investment, service by service.
                                </p>
                            </div>

                            {/* Card 5 */}
                            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-blue-800 text-xl font-semibold mb-2"> Scalable Services</h3>
                                <p className="text-gray-700">
                                    Whether you're a local garage or nationwide repair network, our modular services grow with your business.
                                </p>
                            </div>

                            {/* Card 6 */}
                            <div className="bg-white border border-blue-100 rounded-xl p-6 shadow-md hover:shadow-lg transition">
                                <h3 className="text-blue-800 text-xl font-semibold mb-2"> Dedicated Account Manager</h3>
                                <p className="text-gray-700">
                                    One point of contact. One partner who knows your business and proactively drives your goals forward.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="max-w-7xl mx-auto px-4">
                    <FAQSection data={faqData} />
                </section>

                {/* CTA: Conversion Block */}
                <section className="py-16 bg-[#0f172a] text-white px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
                    <div className="max-w-3xl mx-auto relative z-10">
                        <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
                            Ready to Accelerate Your Garage’s Growth?
                        </h2>
                        <p className="text-lg text-gray-200 mb-8">
                            Book a free, no-pressure strategy session with our experts and see how MM AutoTech can help your business run smoother, sell more, and retain customers.
                        </p>

                        <Link to="/book-appointment" className="inline-block">
                            <button className="bg-yellow-400 text-blue-950 px-8 py-3 text-sm sm:text-base rounded font-bold shadow-md hover:bg-yellow-300 transition-all duration-300">
                                Book My Free Strategy Call
                            </button>
                        </Link>
                    </div>

                    {/* Optional: Background Visual Flair */}
                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/asfalt-dark.png')] opacity-10 z-0" aria-hidden="true" />
                </section>
            </div></>
    );
};

export default Services;



